import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router, RouterModule, RouterOutlet } from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { I18nComponent } from '../../i18n/i18n.component';
import { isHubSpotUrl } from '@app/hubspot/utils/packet-id.util';


@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    TranslateModule,
    I18nComponent,
  ],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.css']
})
export class HeaderComponent implements OnInit, OnDestroy {

  isSidebarOpen = true;
  isDarkMode = false;
  isAppDropdownOpen = false;
  isSearchModalOpen = false;
  isKeyboardShortcutsModalOpen = false;
  breadcrumbs: { label: string; url: string }[] = [];
  isHubSpotShell = false;
  navbarActions: Array<'search' | 'app' | 'language' | 'dark-mode' | 'notifications'> = ['search', 'app', 'language', 'dark-mode', 'notifications'];

  private routerEventsSubscription?: Subscription;
  private languageChangeSubscription?: Subscription;
  searchTerm = '';
  readonly searchTargets: Array<{ label: string; route: string; keywords: string[] }> = [
    { label: 'Categories', route: '/advance/categories', keywords: ['category', 'categories'] },
    { label: 'Participants', route: '/advance/participants', keywords: ['participant', 'participants'] },
    { label: 'Documents', route: '/advance/documents', keywords: ['document', 'documents'] },
    { label: 'Dynamic Functions', route: '/advance/dynamic-functions', keywords: ['dynamic', 'function', 'functions', 'dynamic functions'] },
    { label: 'Rules', route: '/advance/rules', keywords: ['rule', 'rules'] },
    { label: 'Templates', route: '/advance/templates', keywords: ['template', 'templates'] },
    { label: 'Messaging Templates', route: '/advance/messaging-template', keywords: ['message', 'messages', 'messaging', 'messaging template', 'messaging templates'] }
  ];
  readonly keyboardShortcutSections: Array<{
    title: string;
    items: Array<{ label: string; keys: string[]; action: string }>;
  }> = [
      {
        title: 'Navigation',
        items: [
          { label: 'Open search', keys: ['⌘', 'K'], action: 'open-search' },
          { label: 'Go to Dashboard', keys: ['G', 'D'], action: 'go-dashboard' },
          { label: 'Go to Envelopes', keys: ['G', 'E'], action: 'go-envelope' },
          { label: 'Go to Settings', keys: ['G', 'S'], action: 'go-settings' }
        ]
      },
      {
        title: 'Actions',
        items: [
          { label: 'Create new envelope', keys: ['⌘', 'N'], action: 'new-envelope' },
          { label: 'Upload document', keys: ['⌘', 'U'], action: 'upload-document' },
          { label: 'Toggle sidebar', keys: ['⌘', '\\'], action: 'toggle-sidebar' }
        ]
      },
      {
        title: 'General',
        items: [
          { label: 'Toggle dark mode', keys: ['⌘', 'D'], action: 'toggle-dark-mode' },
          { label: 'Show shortcuts', keys: ['?'], action: 'show-shortcuts' },
          { label: 'Close modal / Cancel', keys: ['ESC'], action: 'close-modal' }
        ]
      }
    ];
  private pendingNavigationPrefix: 'g' | null = null;
  private pendingNavigationTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private translateService: TranslateService
  ) { }

  private applyTheme(isDarkMode: boolean) {
    const html = document.documentElement;
    html.classList.remove('dark', 'light');
    html.classList.add(isDarkMode ? 'dark' : 'light');
    html.setAttribute('data-bs-theme', isDarkMode ? 'dark' : 'light');
  }

  ngOnInit() {
    // Initialize dark mode from localStorage
    const savedDarkMode = localStorage.getItem('darkMode');
    this.isDarkMode = savedDarkMode === 'true';
    this.applyTheme(this.isDarkMode);

    this.isHubSpotShell = isHubSpotUrl(this.router.url);
    this.updateBreadcrumbs();
    this.routerEventsSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.isHubSpotShell = isHubSpotUrl(this.router.url);
        this.updateBreadcrumbs();
      });

    this.languageChangeSubscription = this.translateService.onLangChange
      .subscribe(() => this.updateBreadcrumbs());
  }

  ngOnDestroy() {
    this.routerEventsSubscription?.unsubscribe();
    this.languageChangeSubscription?.unsubscribe();
  }

  private updateBreadcrumbs() {
    const breadcrumbs: { label: string; url: string }[] = [];
    let currentRoute = this.activatedRoute.root;
    let currentUrl = '';

    while (currentRoute.firstChild) {
      currentRoute = currentRoute.firstChild;
      const routeSegments = currentRoute.snapshot.url.map((segment) => segment.path).filter(Boolean);

      if (!routeSegments.length) {
        continue;
      }

      routeSegments.forEach((segment) => {
        currentUrl += `/${segment}`;
        breadcrumbs.push({
          label: this.translateBreadcrumbLabel(segment),
          url: currentUrl
        });
      });
    }

    this.breadcrumbs = breadcrumbs;
  }

  private translateBreadcrumbLabel(segment: string) {
    const translationKey = this.getBreadcrumbTranslationKey(segment);

    if (translationKey) {
      const translatedLabel = this.translateService.instant(translationKey);
      if (translatedLabel && translatedLabel !== translationKey) {
        return translatedLabel;
      }
    }

    return this.formatBreadcrumbLabel(segment);
  }

  private getBreadcrumbTranslationKey(segment: string) {
    const normalizedSegment = segment.toLowerCase();
    const segmentTranslationMap: Record<string, string> = {
      dashboard: 'HEADER.DASHBOARD',
      advance: 'HEADER.ADVANCE',
      categories: 'CATEGORY.CATEGORIES',
      category: 'HEADER.CATEGORY',
      rules: 'HEADER.RULES',
      'dynamic-functions': 'HEADER.DYNAMIC_FUNCTIONS',
      functions: 'HEADER.FUNCTIONS',
      documents: 'COMMON.DOCUMENTS',
      document: 'HEADER.DOCUMENT',
      template: 'HEADER.TEMPLATE',
      participant: 'HEADER.PARTICIPANTS',
      participants: 'HEADER.PARTICIPANTS',
      question: 'HEADER.QUESTIONS',
      questions: 'HEADER.QUESTIONS',
      'messaging-template': 'HEADER.MESSAGING_TEMPLATE',
      settings: 'HEADER.SETTINGS',
      profile: 'COMMON.MY_PROFILE',
      help: 'HEADER.HELP',
      envelope: 'HEADER.ENVELOPE',
      forms: 'HEADER.FORMVIZZ',
      basic: 'COMMON.BASIC',
      'add-document': 'COMMON.ADD_DOCUMENT'
    };

    return segmentTranslationMap[normalizedSegment] ?? null;
  }

  private formatBreadcrumbLabel(segment: string) {
    return segment
      .replace(/[-_]/g, ' ')
      .replace(/\b\w/g, (character) => character.toUpperCase());
  }

  toggleSidebar() {
    const sidebar = document.getElementById('sidebar');
    const overlay = document.getElementById('sidebar-overlay');

    if (sidebar) {
      sidebar.classList.toggle('-translate-x-full');
      if (overlay) {
        overlay.classList.toggle('hidden');
      }
    }
  }

  toggleAppDropdown() {
    this.isAppDropdownOpen = !this.isAppDropdownOpen;

    const dropdown = document.getElementById('app-dropdown');
    if (dropdown) {
      if (this.isAppDropdownOpen) {
        dropdown.classList.remove('hidden');
      } else {
        dropdown.classList.add('hidden');
      }
    }

  }

  toggleDarkMode() {
    this.isDarkMode = !this.isDarkMode;
    this.applyTheme(this.isDarkMode);

    // Save preference
    localStorage.setItem('darkMode', this.isDarkMode.toString());
  }

  openSearchModal() {
    this.isSearchModalOpen = true;
    const modal = document.getElementById('search-modal');
    if (modal) {
      modal.classList.remove('hidden');
      const input = document.getElementById('search-input');
      if (input) {
        setTimeout(() => input.focus(), 100);
      }
    }
  }

  closeSearchModal() {
    this.isSearchModalOpen = false;
    const modal = document.getElementById('search-modal');
    if (modal) {
      modal.classList.add('hidden');
    }
  }

  get filteredSearchTargets() {
    const normalizedSearch = this.searchTerm.trim().toLowerCase().replace(/\s+/g, ' ');
    if (!normalizedSearch) {
      return this.searchTargets;
    }

    return this.searchTargets.filter((target) =>
      target.label.toLowerCase().includes(normalizedSearch)
      || target.keywords.some((keyword) => keyword.includes(normalizedSearch) || normalizedSearch.includes(keyword))
    );
  }

  navigateToSearchTarget(route: string) {
    this.closeSearchModal();
    this.router.navigate([route]);
  }

  private resolveSearchTarget(searchText: string) {
    return this.searchTargets.find((target) =>
      target.keywords.some((keyword) => searchText.includes(keyword))
      || target.label.toLowerCase().includes(searchText)
    ) ?? null;
  }

  private getResidualSearchTerm(searchText: string, target: { keywords: string[] }) {
    let residual = searchText;
    for (const keyword of target.keywords) {
      const keywordPattern = new RegExp(`\\b${keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'gi');
      residual = residual.replace(keywordPattern, ' ');
    }
    return residual.trim().replace(/\s+/g, ' ');
  }

  submitSearch() {
    const normalizedSearch = this.searchTerm.trim().replace(/\s+/g, ' ');
    const lowerSearch = normalizedSearch.toLowerCase();

    if (!normalizedSearch) {
      this.closeSearchModal();
      this.router.navigate(['/advance']);
      return;
    }

    const matchedTarget = this.resolveSearchTarget(lowerSearch);
    if (matchedTarget) {
      const residualSearch = this.getResidualSearchTerm(lowerSearch, matchedTarget);
      this.closeSearchModal();

      this.router.navigate([matchedTarget.route], {
        queryParams: residualSearch ? { search: residualSearch } : {}
      });
      return;
    }

    this.closeSearchModal();

    this.router.navigate(['/advance/documents'], {
      queryParams: normalizedSearch ? { search: normalizedSearch } : {}
    });
  }

  closeKeyboardShortcuts() {
    this.isKeyboardShortcutsModalOpen = false;
    const modal = document.getElementById('keyboard-shortcuts-modal');
    if (modal) {
      modal.classList.add('hidden');
      document.body.style.overflow = '';
    }
  }

  openKeyboardShortcuts() {
    this.isKeyboardShortcutsModalOpen = true;
    const modal = document.getElementById('keyboard-shortcuts-modal');
    if (modal) {
      modal.classList.remove('hidden');
      document.body.style.overflow = 'hidden';
    }
  }

  private isTypingTarget(event: KeyboardEvent) {
    return false; // Disable global shortcuts when any key is pressed, regardless of target element
    // const targetElement = event.target as HTMLElement | null;
    // if (!targetElement) return false;

    // const tagName = targetElement.tagName?.toLowerCase();
    // // Common typing targets
    // if (tagName === 'input' || tagName === 'textarea' || targetElement.isContentEditable) return true;

    // // If the target is inside a form, modal, rich-text editor or an element that opted out of shortcuts,
    // // treat it as a typing target so global shortcuts won't run.
    // if (targetElement.closest && targetElement.closest('form, [data-ignore-shortcuts], .rte-editor, .modal')) return true;

    // return false;
  }

  private clearPendingNavigationPrefix() {
    this.pendingNavigationPrefix = null;
    if (this.pendingNavigationTimeoutId) {
      clearTimeout(this.pendingNavigationTimeoutId);
      this.pendingNavigationTimeoutId = null;
    }
  }

  private setPendingNavigationPrefix(prefix: 'g') {
    this.pendingNavigationPrefix = prefix;
    if (this.pendingNavigationTimeoutId) {
      clearTimeout(this.pendingNavigationTimeoutId);
    }

    this.pendingNavigationTimeoutId = setTimeout(() => {
      this.pendingNavigationPrefix = null;
      this.pendingNavigationTimeoutId = null;
    }, 1200);
  }

  private triggerShortcutAction(action: string) {
    switch (action) {
      case 'open-search':
        this.openSearchModal();
        break;
      case 'go-dashboard':
        this.router.navigate(['/dashboard']);
        break;
      case 'go-envelope':
        this.router.navigate(['/envelope']);
        break;
      case 'go-settings':
        this.router.navigate(['/settings']);
        break;
      case 'new-envelope':
        this.router.navigate(['/forms/basic']);
        break;
      case 'upload-document':
        this.router.navigate(['/documents/add-document']);
        break;
      case 'toggle-sidebar':
        this.toggleSidebar();
        break;
      case 'toggle-dark-mode':
        this.toggleDarkMode();
        break;
      case 'show-shortcuts':
        this.openKeyboardShortcuts();
        break;
      case 'close-modal':
        if (this.isSearchModalOpen) {
          this.closeSearchModal();
        }
        if (this.isKeyboardShortcutsModalOpen) {
          this.closeKeyboardShortcuts();
        }
        break;
    }
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyboardShortcuts(event: KeyboardEvent) {
    return
    const key = event.key.toLowerCase();
    const hasPrimaryModifier = event.metaKey || event.ctrlKey;
    const isTyping = this.isTypingTarget(event);

    if (event.key === 'Escape') {
      this.clearPendingNavigationPrefix();
      this.triggerShortcutAction('close-modal');
      return;
    }

    if (hasPrimaryModifier && key === 'k') {
      event.preventDefault();
      this.clearPendingNavigationPrefix();
      this.triggerShortcutAction('open-search');
      return;
    }

    if (hasPrimaryModifier && key === 'n') {
      event.preventDefault();
      this.clearPendingNavigationPrefix();
      this.triggerShortcutAction('new-envelope');
      return;
    }

    if (hasPrimaryModifier && key === 'u') {
      event.preventDefault();
      this.clearPendingNavigationPrefix();
      this.triggerShortcutAction('upload-document');
      return;
    }

    if (hasPrimaryModifier && key === '\\') {
      event.preventDefault();
      this.clearPendingNavigationPrefix();
      this.triggerShortcutAction('toggle-sidebar');
      return;
    }

    if (hasPrimaryModifier && key === 'd') {
      event.preventDefault();
      this.clearPendingNavigationPrefix();
      this.triggerShortcutAction('toggle-dark-mode');
      return;
    }

    if (!hasPrimaryModifier && !event.altKey && event.key === '?') {
      event.preventDefault();
      this.clearPendingNavigationPrefix();
      this.triggerShortcutAction('show-shortcuts');
      return;
    }

    if (isTyping) {
      return;
    }

    if (!hasPrimaryModifier && !event.altKey && key === 'g') {
      event.preventDefault();
      this.setPendingNavigationPrefix('g');
      return;
    }

    if (this.pendingNavigationPrefix === 'g' && !hasPrimaryModifier && !event.altKey) {
      if (key === 'd') {
        event.preventDefault();
        this.clearPendingNavigationPrefix();
        this.triggerShortcutAction('go-dashboard');
        return;
      }
      if (key === 'e') {
        event.preventDefault();
        this.clearPendingNavigationPrefix();
        this.triggerShortcutAction('go-envelope');
        return;
      }
      if (key === 's') {
        event.preventDefault();
        this.clearPendingNavigationPrefix();
        this.triggerShortcutAction('go-settings');
        return;
      }
      this.clearPendingNavigationPrefix();
    }
  }
}